import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import NavBar from '../../components/navBar';
import ProvinceSelection from './components/ProvinceSelection';
import ClubSelection from './components/ClubSelection';
import ConfirmationScreen from './components/ConfirmationScreen';
import PaymentModal from './components/PaymentModal';

const RisingStarsRegistration = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Registration data
  const [registrationData, setRegistrationData] = useState({
    selectedProvince: null,
    selectedClub: null,
    seasonYear: new Date().getFullYear(),
    status: 'pending'
  });

  // Payment data
  const [paymentData, setPaymentData] = useState({
    email: '',
    phone: '',
    operator: 'mtn',
    amountPaid: 210.00
  });

  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle, processing, success, error

  // Check if user is authenticated
  const isAuthenticated = localStorage.getItem('token') !== null;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
  }, [isAuthenticated, navigate]);

  const handleProvinceSelect = (province) => {
    setRegistrationData(prev => ({
      ...prev,
      selectedProvince: province
    }));
    setCurrentStep(2);
  };

  const handleClubSelect = (club) => {
    setRegistrationData(prev => ({
      ...prev,
      selectedClub: club
    }));
    setCurrentStep(3);
  };

  const handleConfirmation = () => {
    setCurrentStep(4);
  };

  const generateReference = () => {
    const timestamp = Date.now();
    const randomNum = Math.floor(Math.random() * 1000);
    return `RS-${timestamp}-${randomNum}`;
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setPaymentStatus('processing');
    setError(null);

    try {
      const referenceId = generateReference();
      
      // 1. Create registration record in database
      const registrationResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/registrations`,
        {
          clubId: registrationData.selectedClub.clubId,
          leagueId: registrationData.selectedProvince.leagueId,
          seasonYear: registrationData.seasonYear,
          status: 'pending',
          paymentMethod: paymentData.operator,
          paymentRef: referenceId,
          amountPaid: paymentData.amountPaid
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      // 2. Initiate payment with Lenco
      const paymentResponse = await axios.post(
        "https://payment-lenco-api.onrender.com/api/payment/request",
        {
          amount: paymentData.amountPaid,
          reference: referenceId,
          phone: paymentData.phone,
          operator: paymentData.operator,
          country: 'zm',
          bearer: 'customer'
        }
      );

      // 3. Start polling for payment verification
      const verifyPayment = async () => {
        try {
          const verifyResponse = await axios.get(
            `https://payment-lenco-api.onrender.com/api/payment/verify/${referenceId}`
          );
          
          if (verifyResponse.data.status === true && verifyResponse.data.data.status === 'successful') {
            // Update registration status to paid
            await axios.patch(
              `${process.env.REACT_APP_API_URL}/registrations/${registrationResponse.data.registrationId}/payment`,
              {
                status: 'paid',
                paymentMethod: paymentData.operator,
                paymentRef: referenceId,
                amountPaid: paymentData.amountPaid
              },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
              }
            );

            setPaymentStatus('success');
            setTimeout(() => {
              navigate('/desac-sports/registration-success');
            }, 2000);
          } else {
            // Continue polling if payment is still pending
            setTimeout(verifyPayment, 5000);
          }
        } catch (error) {
          console.error('Error verifying payment:', error);
          setPaymentStatus('error');
          setError('Payment verification failed. Please try again.');
        }
      };

      // Start verification polling
      verifyPayment();

    } catch (error) {
      console.error('Error processing payment:', error);
      setPaymentStatus('error');
      setError('Payment failed. Please try again.');
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <ProvinceSelection 
            onProvinceSelect={handleProvinceSelect}
            loading={loading}
          />
        );
      case 2:
        return (
          <ClubSelection 
            selectedProvince={registrationData.selectedProvince}
            onClubSelect={handleClubSelect}
            onBack={handleBack}
            loading={loading}
          />
        );
      case 3:
        return (
          <ConfirmationScreen 
            registrationData={registrationData}
            onConfirm={handleConfirmation}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <PaymentModal 
            registrationData={registrationData}
            paymentData={paymentData}
            setPaymentData={setPaymentData}
            onSubmit={handlePaymentSubmit}
            paymentStatus={paymentStatus}
            error={error}
            onBack={handleBack}
          />
        );
      default:
        return null;
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <NavBar />
      <div className="container mx-auto px-4 py-8 mt-16">
        {/* Progress Indicator */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                  currentStep >= step 
                    ? 'bg-red-600 text-white' 
                    : 'bg-gray-700 text-gray-400'
                }`}>
                  {step}
                </div>
                {step < 4 && (
                  <div className={`w-16 h-1 mx-2 ${
                    currentStep > step ? 'bg-red-600' : 'bg-gray-700'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-sm text-gray-400">
            <span>Select Province</span>
            <span>Select Club</span>
            <span>Confirm Details</span>
            <span>Payment</span>
          </div>
        </div>

        {/* Current Step Content */}
        {renderCurrentStep()}
      </div>
    </div>
  );
};

export default RisingStarsRegistration; 